import { ChangeEvent, useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import { IMAGE_LIMIT_SIZE_10MB, IMAGE_LIMIT_SIZE_3MB } from '@lib/constant';

type FileUploadProps = {
    limitSize?: number;
    maxFileLength?: number;
};

const useFileUpload = ({
    limitSize = IMAGE_LIMIT_SIZE_3MB,
    maxFileLength = 3
}: FileUploadProps = {}) => {
    const [file, setFile] = useState<File | null>(null); // 파일 단일 업로드
    const [previewUrl, setPreviewUrl] = useState('');
    const [multipleFiles, setMultipleFiles] = useState<File[]>([]); //파일 다중 업로드

    const validation = useCallback(
        (file: File) => {
            if (file.size > limitSize) {
                switch (limitSize) {
                    case IMAGE_LIMIT_SIZE_3MB:
                        toast.error('업로드 가능한 용량은 3MB 미만입니다.');
                        break;
                    case IMAGE_LIMIT_SIZE_10MB:
                        toast.error('업로드 가능한 용량은 10MB 미만입니다.');
                        break;
                }
                return false;
            } else {
                return true;
            }
        },
        [limitSize]
    );
    // 파일 단일 업로드
    const onChangeFile = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const file = (e.currentTarget.files as FileList)[0];
            if (!file) return;
            if (!validation(file)) return;
            setFile(file);
            setPreviewUrl(URL.createObjectURL(file));
            e.target.value = '';
        },
        [validation]
    );
    // 단일 파일 삭제
    const onDeleteFile = useCallback(() => {
        setFile(null);
        setPreviewUrl('');
    }, []);

    // 다중 파일 업로드
    const addMultipleFiles = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(e.currentTarget.files as FileList);
            const attFileCnt = multipleFiles.length; // 기존 추가된 첨부파일 개수
            const remainFileCnt = maxFileLength - attFileCnt; // 추가로 첨부가능한 개수
            const curFileCnt = files.length; // 현재 선택된 첨부파일 개수

            if (curFileCnt > remainFileCnt) {
                toast.error(
                    `첨부파일은 최대 ${maxFileLength}개 까지 첨부 가능합니다.`
                );
            } else {
                for (const file of files) {
                    // 첨부파일 검증
                    if (validation(file)) {
                        // 파일 배열에 담기
                        let reader = new FileReader();
                        reader.onload = function () {
                            setMultipleFiles((prev) => [...prev, file]);
                        };
                        reader.readAsDataURL(file);
                    } else {
                        continue;
                    }
                }
            }
        },
        [multipleFiles, maxFileLength, validation]
    );

    // 다중 파일 삭제
    const deleteMultipleFiles = useCallback((name: string) => {
        setMultipleFiles((prev) => prev.filter((file) => file.name !== name));
    }, []);

    return {
        file,
        setFile,
        previewUrl,
        setPreviewUrl,
        onChangeFile,
        onDeleteFile,
        addMultipleFiles,
        multipleFiles,
        deleteMultipleFiles
    };
};

export default useFileUpload;
